/*
 * DIE GEHALTENE BUEHNE VOM SERVER UEBERNEHMEN (Rafael, 06.10.2026: „explizit Bühnen
 * halten, auch wenn ich woanders weiterarbeite — damit das autonom gemacht werden kann").
 *
 * Der Server sagt, welche Buehne gehalten wird (server/routes/buehne-halten.ts); diese App
 * heftet sie daraufhin selbst an (lib/buehnenAnheften.ts) — versteckt montiert, damit
 * eval/click/type ihre Kacheln erreichen, egal welche Buehne oder welcher Arbeitsbereich
 * vorne steht und auch nachdem Rafael geantwortet hat.
 *
 * Gelesen wird beim Start, auf `control:buehne-halten` und alle 20 s (ein verpasster
 * Rundruf darf den Halt nicht verschlucken). Folgt der Server der Abloesungskette, wird
 * nur `kette` nachgetragen — die montierten Fenster bleiben, wie sie sind.
 */
import { useEffect, useRef } from 'react';
import { leseAngeheftet, setzeAngeheftet, type AngehefteteBuehne } from './buehnenAnheften';
import { buehnenWirt } from './buehnenWirt';
import { layoutAusRezept } from '../components/GuidePanel';
import type { BuehneAnsicht } from '../components/GuideHof';
import { buehnenLeseUrl } from '../../shared/schnellbuehnen';

export interface HaltAnsicht {
  gehalten: { buehneId: string; von: string; seit: string } | null;
  kette: string[];
  aktuell: string | null;
  fehler?: string;
}

export type HaltSchritt =
  | { art: 'nichts' }
  | { art: 'loesen' }
  | { art: 'kette'; kette: string[] }
  | { art: 'anheften'; buehneId: string; kette: string[] };

/** Rein und testbar: was folgt aus Serverstand + eigenem Anheft-Stand? */
export function haltSchritt(server: HaltAnsicht, lokal: AngehefteteBuehne | null): HaltSchritt {
  if (!server.gehalten) return lokal?.quelle === 'server' ? { art: 'loesen' } : { art: 'nichts' };
  const kette = server.kette.length ? server.kette : [server.gehalten.buehneId];
  const lokaleGlieder = lokal ? [...new Set([lokal.id, ...(lokal.kette ?? [])])] : [];
  if (lokal && lokaleGlieder.some(id => kette.includes(id))) {
    const neu = [...new Set([...lokaleGlieder, ...kette])];
    return neu.length === lokaleGlieder.length && lokal.quelle === 'server' && lokal.kette ? { art: 'nichts' } : { art: 'kette', kette: neu };
  }
  return { art: 'anheften', buehneId: server.aktuell ?? kette[kette.length - 1], kette };
}

async function holeJson<T>(url: string): Promise<T> {
  const r = await fetch(url, { signal: AbortSignal.timeout(10000) });
  if (!r.ok) throw new Error(`${url}: HTTP ${r.status}`);
  return r.json() as Promise<T>;
}

export function useBuehneHalten(opts: {
  aktiv: boolean;
  nutzer: string;
  projektIds: readonly string[];
  addMessageHandler: (h: (msg: any) => void) => () => void;
}): void {
  const { aktiv, nutzer, addMessageHandler } = opts;
  const projekteRef = useRef(opts.projektIds); projekteRef.current = opts.projektIds;
  const laeuft = useRef(false);
  const nochmal = useRef(false);

  useEffect(() => {
    if (!aktiv) return;
    let weg = false;
    const abgleichen = async (): Promise<void> => {
      if (laeuft.current) { nochmal.current = true; return; }
      laeuft.current = true;
      try {
        do {
          nochmal.current = false;
          const server = await holeJson<HaltAnsicht>('/api/buehne-halten');
          if (weg) return;
          if (server.fehler) console.error('[buehne-halten]', server.fehler);
          const lokal = leseAngeheftet(nutzer);
          const schritt = haltSchritt(server, lokal);
          if (schritt.art === 'loesen') setzeAngeheftet(nutzer, null);
          else if (schritt.art === 'kette' && lokal) setzeAngeheftet(nutzer, { ...lokal, quelle: 'server', kette: schritt.kette });
          else if (schritt.art === 'anheften') {
            const b = await holeJson<BuehneAnsicht>(buehnenLeseUrl(schritt.buehneId));
            if (weg) return;
            if (!b.rezept) throw new Error(`Gehaltene Bühne ${b.id} hat kein Rezept — nichts zu montieren.`);
            if (!b.workspace) throw new Error(`Gehaltene Bühne ${b.id} nennt keinen Arbeitsbereich.`);
            const { doc } = layoutAusRezept(b.rezept, null, undefined, b.haus);
            const projectId = buehnenWirt(b.workspace, projekteRef.current, null).projectId;
            if (!projectId) throw new Error(`Für die gehaltene Bühne ${b.id} gibt es hier keinen Arbeitsbereich.`);
            setzeAngeheftet(nutzer, {
              id: b.id, projectId, doc, bezug: { id: b.id, textblock: b.textblock },
              seit: Date.now(), quelle: 'server', kette: [...new Set([...schritt.kette, b.id])],
            });
            console.log(`[buehne-halten] ${b.id} angeheftet (Kette ${schritt.kette.join(' → ')})`);
          }
        } while (nochmal.current && !weg);
      } catch (e) {
        console.error('[buehne-halten] Abgleich fehlgeschlagen:', e);
      } finally {
        laeuft.current = false;
      }
    };
    void abgleichen();
    const uhr = setInterval(() => void abgleichen(), 20_000);
    const ab = addMessageHandler((msg: any) => { if (msg?.type === 'control:buehne-halten') void abgleichen(); });
    return () => { weg = true; clearInterval(uhr); ab(); };
  }, [aktiv, nutzer, addMessageHandler]);
}

/** Der Knopf an der Buehne geht ueber den Server — dann gilt dieselbe Wahrheit fuer API und Hand. */
export async function halteAmServer(buehneId: string | null, von: string): Promise<void> {
  const r = buehneId
    ? await fetch('/api/buehne-halten', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ buehneId, von }) })
    : await fetch(`/api/buehne-halten?von=${encodeURIComponent(von)}`, { method: 'DELETE' });
  if (!r.ok) throw new Error(`Halten am Server: HTTP ${r.status} ${(await r.text()).slice(0, 200)}`);
}
